import { useRef } from 'react';
import type { MouseEvent } from 'react';
import { useAtomValue } from 'jotai';
import { cva } from 'class-variance-authority';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { ReactComponent as PlayIcon } from '@rocco/icons/svg/play.svg';
import { Button } from '@rocco/components/button';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { EDITORIAL_GRADE_MEDIA, EditorialGradeWash } from '../editorial-grade';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';
import { formatDateToDDMMYY } from '@rocco/utils/format-date';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { trackTileMediaDetailClick } from '@archipro-website/web/app/modules/tracking/util/trackTileMedia';
import { useTracker } from '../../../../../tracker/src/react-bindings/context';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { uiContextAtomArticleViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import {
    ARTICLE_HERO_TRANSITION_NAME,
    recordTileMorphStart,
    startsPlainNavigation,
    usePrefersReducedMotion,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';

const _articleTileContentVariants = cva('', {
    variants: {
        variant: {
            featured:
                'absolute inset-x-0 bottom-0 flex items-end justify-between gap-18 px-[1.83rem] pb-[2.12rem] text-gray-50',
            default: 'pt-4 text-[#515151]',
            // 10px image-to-text gap, 30px right inset (Figma 49:4497). The
            // inset is a desktop measure: on a phone's half-column card it
            // ate a fifth of the caption's width, so below md the text runs
            // to the card edge.
            editorial: 'pt-[0.625rem] text-[#1f1f21] md:pr-[1.875rem]',
            // Directory card: 38.5px right inset (Figma 60:2077), also md+
            // only. The image-to-text run-in is half the drawn 30px — at full
            // size the section-and-byline line floated between the picture
            // and the headline rather than belonging to either.
            editorialLarge: 'pt-[0.9375rem] text-[#1f1f21] md:pr-[2.4066rem]',
        },
    },
    defaultVariants: {
        variant: 'featured',
    },
});

const _articleTileTitleVariants = cva(
    'text-base font-medium tracking-[0.02rem]',
    {
        variants: {
            variant: {
                featured: 'pr-[5rem]',
                default: 'line-clamp-3 text-gray-900',
                // Gestura Headline 24/32, -0.24px, capitalised (Figma 49:4497).
                editorial:
                    'line-clamp-3 font-gestura text-[1.5rem] leading-[2rem] font-normal tracking-[-0.015rem] text-[#1f1f21] capitalize',
                editorialLarge:
                    'line-clamp-3 font-gestura text-[1.5rem] leading-[2rem] font-normal tracking-[-0.015rem] text-[#1f1f21] capitalize',
            },
        },
        defaultVariants: {
            variant: 'featured',
        },
    },
);

const _articleTileMetaVariants = cva(
    'line-clamp-1 text-3.5 leading-[1.1375rem] font-normal tracking-[0.0175rem] text-ellipsis md:text-[0.875rem]',
    {
        variants: {
            variant: {
                featured: '',
                default: '',
                // Set as it is written rather than shouted: the line names a
                // section and a person, and in caps it competed with the
                // headline under it. Dropping the caps was the whole of it —
                // the size stays where it was, and the generous tracking that
                // went with the caps comes back to normal.
                // 12px below md, the category tiles' meta size; 14px from md.
                // No clamp below md: section and byline render as their own
                // truncating lines there, so the author always shows. md+
                // clamps the joined line to one.
                editorial:
                    'line-clamp-none text-3 leading-4 tracking-[0.02rem] text-[#1f1f21] md:line-clamp-1 md:text-[0.875rem] md:leading-[1.25rem]',
                editorialLarge:
                    'line-clamp-none text-3 leading-4 tracking-[0.02rem] text-[#1f1f21] md:line-clamp-1 md:text-[0.875rem] md:leading-[1.25rem]',
            },
        },
        defaultVariants: {
            variant: 'featured',
        },
    },
);

/**
 * The standfirst under the title on the editorial cards. Sentence case and set
 * in the body face, so it reads as the article's opening line rather than as a
 * second label.
 */
const _articleTileStandfirstVariants = cva('', {
    variants: {
        variant: {
            featured: 'hidden',
            default: 'hidden',
            // Hidden below md: the phone cards are their picture, byline and
            // title everywhere the editorial card appears.
            editorial:
                'mt-[0.625rem] hidden font-sans text-[0.9375rem] leading-[1.375rem] font-normal text-[#1f1f21] md:line-clamp-3',
            editorialLarge:
                'mt-[0.625rem] hidden font-sans text-[0.9375rem] leading-[1.375rem] font-normal text-[#1f1f21] md:line-clamp-3',
        },
    },
    defaultVariants: { variant: 'featured' },
});

const _articleTileTextStackVariants = cva('flex flex-col', {
    variants: {
        variant: {
            featured: 'gap-3 md:gap-2.25',
            default: 'gap-3 md:gap-2.25',
            // Half the drawn 20px, so the section-and-byline line sits with
            // the headline it belongs to. The standfirst puts the full gap
            // back under the title — see _articleTileStandfirstVariants.
            editorial: 'gap-[0.625rem]',
            editorialLarge: 'gap-[0.625rem]',
        },
    },
    defaultVariants: {
        variant: 'featured',
    },
});

/**
 * The frame wrapping the media and the content block. `editorial` becomes a
 * flex column so the image takes the space the title leaves, which is what
 * keeps every card in a row the same height and their meta lines aligned.
 */
const _articleTileFrameVariants = cva('group relative h-full', {
    variants: {
        variant: {
            featured: '',
            default: '',
            editorial: '',
            editorialLarge: '',
        },
    },
    defaultVariants: {
        variant: 'featured',
    },
});

const _articleButtonVariants = cva('', {
    variants: {
        variant: {
            featured: '',
            default: 'hidden',
            editorial: 'hidden',
            editorialLarge: 'hidden',
        },
    },
});

/** The frame's own wording for a tile whose banner is a film (Figma 60:2086). */
const FILM_TILE_LABEL = 'Film Addition';

export const ArticleTile = (
    props: TileViewModel<ArticleTileViewModel> & {
        /**
         * Overrides the editorial card's locked image box. A card spanning two
         * grid columns needs a landscape ratio to keep the same image height as
         * its single-column neighbours — at the shared portrait ratio, twice
         * the width means twice the height.
         */
        mediaClassName?: string;
        /**
         * Overrides the title's type. A single-column editorial card on a
         * phone carries half the measure of a spanning one, so the host
         * grid steps its title down while the wide card keeps the full cut.
         */
        titleClassName?: string;
        /**
         * Leaves the editorial card's standfirst out, so the card is its
         * picture, its eyebrow and its title — the directory's grid, where
         * three opening lines under every card read as a wall of copy.
         */
        hideStandfirst?: boolean;
        /**
         * Grades the picture onto the editorial palette — see
         * `editorial-grade`. For the directory's grids, where pictures from
         * every hand sit side by side.
         */
        graded?: boolean;
        /**
         * The card's real rendered width for the srcset, where the host grid
         * knows it. Without it the media falls back to the generic per-aspect
         * estimate, which over-fetches on narrow grid cells.
         */
        mediaSizes?: string;
        /**
         * Loads the picture with the page rather than on approach. For cards
         * the reader lands on — the leading run of a listing.
         */
        eager?: boolean;
    },
) => {
    const {
        aspectRatio,
        mediaClassName,
        titleClassName,
        hideStandfirst = false,
        graded = false,
        mediaSizes,
        eager,
        rounded = true,
        model: article,
        enableTileSlider,
        isFollowed,
        onFollow,
        routeState,
        hideFollowButton = false,
        followButton,
        overlay,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        section,
    } = props;
    const { variant = 'featured' } = article;
    const tracker = useTracker();
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    // The morph runs wherever the tile does — the flag is its only gate, so
    // a related tile on a detail page zooms the same way a directory tile
    // does. Reduced motion opts out of the snapshot capture altogether, as
    // the product tile does. The article-specific atom also requires the
    // article detail revamp, because only the revamp page carries the
    // `article-hero` counterpart.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomArticleViewTransitionsEnabled,
    );
    const prefersReducedMotion = usePrefersReducedMotion();
    const viewTransitionsEnabled =
        viewTransitionsFlagOn && !prefersReducedMotion;
    // The picture box flies into the article's opening picture.
    const heroStyle = useTileHeroViewTransitionStyle(
        disableLink || !viewTransitionsEnabled ? undefined : article.link,
        ARTICLE_HERO_TRANSITION_NAME,
    );

    const handleTileClick = (event?: MouseEvent<HTMLAnchorElement>) => {
        onClick?.();
        if (disableLink) {
            return;
        }
        if (
            viewTransitionsEnabled &&
            (!event || startsPlainNavigation(event))
        ) {
            recordTileMorphStart(
                ARTICLE_HERO_TRANSITION_NAME,
                article.link,
                tileRef.current?.querySelector('img')?.currentSrc,
            );
        }
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            article.id,
            article.professional.id,
            section,
        );
        trackTileMediaDetailClick(tracker, {
            itemID: article.id,
            itemType: 'ArticleTile',
            itemName: article.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={article.link}
            prefetch="intent"
            viewTransition={viewTransitionsEnabled}
            state={routeState}
            onClick={handleTileClick}
        >
            <div className="h-full">
                <div className={cn('flex h-full flex-col gap-4', className)}>
                    <div className={_articleTileFrameVariants({ variant })}>
                        {variant === 'editorial' ||
                        variant === 'editorialLarge' ? (
                            <div
                                /*
                                 * A locked aspect, not a flexed one, and the
                                 * same one on both editorial variants.
                                 *
                                 * 437x524 is the only image box the design
                                 * states (60:2083). The category card's own
                                 * image is `flex: 1 0 0` inside a 622-tall
                                 * frame, so it has no ratio of its own — it
                                 * takes whatever the title leaves, which is
                                 * three lines in every mock. Measuring that
                                 * gives 432x478, a near-square that is an
                                 * artefact of the mock copy rather than a
                                 * decision, and with real titles it would give
                                 * every card a different image height and the
                                 * grid would stop reading as a grid.
                                 */
                                // The corner goes on this box rather than on
                                // the media inside it: this is what clips, so
                                // rounding the media alone left the square
                                // corners of this one showing behind it.
                                className={cn(
                                    'relative aspect-[437/524] w-full overflow-hidden rounded-tile',
                                    graded && EDITORIAL_GRADE_MEDIA,
                                    mediaClassName,
                                )}
                                style={heroStyle}
                            >
                                <TileMedia
                                    aspectRatio={aspectRatio}
                                    rounded={rounded}
                                    images={[article.image]}
                                    video={article.video}
                                    enableTileSlider={enableTileSlider}
                                    gradiantOverlay={false}
                                    sizes={mediaSizes}
                                    eager={eager}
                                />
                                {graded ? <EditorialGradeWash /> : null}
                                {/* Top-left of the picture, over it rather
                                    than under it with the meta line: this
                                    says the card opens somewhere else, so it
                                    has to be read before the card is
                                    followed, not after. */}
                                {article.isPremium && (
                                    <span className="pointer-events-none absolute top-[1rem] left-[1rem] rounded-full bg-white/90 px-[0.75rem] py-[0.375rem] font-sans text-[0.6875rem] leading-none font-medium tracking-[0.06em] text-[#181818] uppercase backdrop-blur-md">
                                        Premium article
                                    </span>
                                )}
                                {/* Inside the picture, not beside it: on these
                                    variants the caption is stacked under the
                                    media rather than laid over it, so an
                                    overlay pinned to the frame would cover the
                                    words. */}
                                {overlay}
                                {/*
                                 * Figma 60:2082. The glyph box is 38.59% of the
                                 * card's width, which is the width the frame's
                                 * triangle occupies once Rocco's play icon —
                                 * the same shape, to the same 13:15 — is scaled
                                 * to it. Sized off the card so it holds at both
                                 * editorial widths.
                                 */}
                                {article.hasVideo && (
                                    <div className="pointer-events-none absolute inset-0">
                                        <div
                                            aria-hidden={true}
                                            className="absolute inset-0 flex items-center justify-center"
                                        >
                                            {/*
                                             * One size on every card, not a
                                             * share of the width: sized off
                                             * the card it came out larger on a
                                             * double than on a single beside
                                             * it, so a row of films read as
                                             * two different controls.
                                             *
                                             * A blurred white disc rather than
                                             * a bare white glyph. The glyph
                                             * alone needed a heavy drop shadow
                                             * to survive a bright still; the
                                             * disc carries itself over any
                                             * photograph, and the ink on it is
                                             * the page's own.
                                             */}
                                            <span className="flex size-[3rem] items-center justify-center rounded-full bg-white/80 backdrop-blur-md">
                                                <PlayIcon className="h-auto w-[1.125rem] fill-[#181818]" />
                                            </span>
                                        </div>
                                        <span
                                            className="absolute inset-x-0 text-center font-sans text-[1.2918rem] leading-[1.25] font-medium tracking-[-0.0323rem] text-white"
                                            style={{ top: '68.32%' }}
                                        >
                                            {FILM_TILE_LABEL}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <TileMedia
                                aspectRatio={aspectRatio}
                                rounded={rounded}
                                images={[article.image]}
                                video={article.video}
                                enableTileSlider={enableTileSlider}
                                gradiantOverlay={true}
                                sizes={mediaSizes}
                                eager={eager}
                            />
                        )}
                        {/* Already placed inside the media box on the
                            editorial variants. */}
                        {overlay ? (
                            variant === 'editorial' ||
                            variant === 'editorialLarge' ? null : (
                                overlay
                            )
                        ) : (
                            <div className="absolute top-3.5 right-4 flex gap-1 md:invisible md:group-hover:visible">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton && (
                                          <TileFollowButton
                                              pinId={article.id}
                                              pinType="ArticlePin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        )}

                        {article.badge && (
                            <div className="absolute top-4 left-4">
                                <TileBadge title="Article" />
                            </div>
                        )}

                        <div
                            className={cn(
                                _articleTileContentVariants({ variant }),
                                TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            )}
                        >
                            {/* minmax(0,…): the byline lines are nowrap
                                truncators, and an auto-min track would take
                                their full-text min-content and push the
                                caption past the card (visible wherever the
                                card isn't in a fixed grid column, e.g. a
                                carousel). */}
                            <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
                                <div
                                    className={_articleTileTextStackVariants({
                                        variant,
                                    })}
                                >
                                    {(variant === 'editorial' ||
                                        variant === 'editorialLarge') && (
                                        // Section and byline, over the title:
                                        // it says what kind of piece this is
                                        // before the reader commits to reading
                                        // the headline.
                                        <span
                                            // Through `cn` so the variant's own
                                            // size wins: the shared base sets a
                                            // md: size too, and side by side in
                                            // one string the base was the one
                                            // that applied.
                                            className={cn(
                                                _articleTileMetaVariants({
                                                    variant,
                                                }),
                                            )}
                                        >
                                            {/* Below md the two parts stack
                                                as truncating lines with no
                                                separator — a wrapped line
                                                opening on a floating dot read
                                                as a typo. md+ joins them on
                                                one line with the dot. */}
                                            {article.categoryLabel && (
                                                <span className="block truncate md:inline md:overflow-visible md:whitespace-normal">
                                                    {article.categoryLabel}
                                                </span>
                                            )}
                                            {article.categoryLabel &&
                                                article.label && (
                                                    <span className="hidden md:inline">
                                                        {' · '}
                                                    </span>
                                                )}
                                            {article.label && (
                                                <span className="block truncate md:inline md:overflow-visible md:whitespace-normal">
                                                    {article.label}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                    <span
                                        className={cn(
                                            _articleTileTitleVariants({
                                                variant,
                                            }),
                                            titleClassName,
                                            contentEditable?.enabled.title &&
                                                'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                                        )}
                                        contentEditable={
                                            contentEditable?.enabled.title
                                        }
                                        role={
                                            contentEditable?.enabled.title
                                                ? 'textbox'
                                                : undefined
                                        }
                                        aria-label={
                                            contentEditable?.enabled.title
                                                ? 'Edit title'
                                                : undefined
                                        }
                                        aria-multiline="false"
                                        suppressContentEditableWarning={true}
                                        onInput={(
                                            e: React.FormEvent<HTMLSpanElement>,
                                        ) =>
                                            contentEditable?.onChange?.(
                                                (e.target as HTMLSpanElement)
                                                    .textContent ?? '',
                                                'title',
                                            )
                                        }
                                        onBlur={() => {
                                            contentEditable?.onBlur?.();
                                        }}
                                        onKeyDown={(
                                            e: React.KeyboardEvent<HTMLSpanElement>,
                                        ) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                (
                                                    e.target as HTMLSpanElement
                                                ).blur();
                                            }
                                        }}
                                    >
                                        {article.title}
                                    </span>
                                    <span
                                        className={cn(
                                            variant === 'editorial' ||
                                                variant === 'editorialLarge'
                                                ? _articleTileStandfirstVariants(
                                                      { variant },
                                                  )
                                                : _articleTileMetaVariants({
                                                      variant,
                                                  }),
                                            // Kept for an editor, who is here
                                            // to change the very line the
                                            // reader is spared.
                                            hideStandfirst &&
                                                !contentEditable?.enabled
                                                    .description &&
                                                'hidden',
                                            contentEditable?.enabled
                                                .description &&
                                                'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                                        )}
                                        contentEditable={
                                            contentEditable?.enabled.description
                                        }
                                        role={
                                            contentEditable?.enabled.description
                                                ? 'textbox'
                                                : undefined
                                        }
                                        aria-label={
                                            contentEditable?.enabled.description
                                                ? 'Edit description'
                                                : undefined
                                        }
                                        aria-multiline="false"
                                        suppressContentEditableWarning={true}
                                        onInput={(
                                            e: React.FormEvent<HTMLSpanElement>,
                                        ) =>
                                            contentEditable?.onChange?.(
                                                (e.target as HTMLSpanElement)
                                                    .textContent ?? '',
                                                'description',
                                            )
                                        }
                                        onBlur={() => {
                                            contentEditable?.onBlur?.();
                                        }}
                                        onKeyDown={(
                                            e: React.KeyboardEvent<HTMLSpanElement>,
                                        ) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                (
                                                    e.target as HTMLSpanElement
                                                ).blur();
                                            }
                                        }}
                                    >
                                        {variant === 'editorial' ||
                                        variant === 'editorialLarge'
                                            ? article.introText
                                            : `${
                                                  article.date
                                                      ? `${formatDateToDDMMYY(article.date)} - `
                                                      : ''
                                              }Written by ${article.label}`}
                                    </span>
                                </div>
                                <div
                                    className={_articleButtonVariants({
                                        variant,
                                    })}
                                >
                                    <Button
                                        size="square42"
                                        color="primary"
                                        aria-label="Read article"
                                    >
                                        <ArrowRightIcon />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {inlineActionButtons && (
                    <div className="mt-4 flex flex-col">
                        {inlineActionButtons}
                    </div>
                )}
            </div>
        </OptionalLink>
    );
};
